/**
 * Lets a signed-in account into the app only once it is FULLY SET UP (`appEntryFor`, the rule in
 * `src/lib/account-setup.ts`, the same one the backend and the database apply). Owner decision,
 * 2026-10-13: "signed in means either one of any pre defined roles with their checks and all, and
 * basically full setup account."
 *
 *   set up                 pass: admin; a seller with a label, or an active one (an organization's
 *                          member with no label of its own); an active buyer or negotiator
 *   blocked                "blocked": pending approval, rejected or suspended. The account-status
 *                          gate parks it on /pending-approval, as before; never the wizard
 *   anything else          redirect to /onboarding, carrying the destination they were trying to
 *                          reach so onboarding can return them to it
 *
 * It used to let an account in once it had an organization or a label, whatever its status. That
 * let a draft buyer who had created an organization but never finished into an app whose catalog
 * its database policies now hide from it. Onboarding picks such an account up where it stopped.
 *
 * Fails CLOSED. A profile fetch that errors used to pass the user through, which silently
 * defeated the mandatory tier for exactly the un-onboarded users this guard exists to catch.
 * See docs/superpowers/specs/2026-08-01-public-signup-routing-design.md §6.
 */
import { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { UserCacheService } from "@/lib/user-cache";
import { appEntryFor } from "@/lib/account-setup";
import { buildOnboardingUrlWithReturn } from "@/lib/auth-redirect";
import { GuardLoadingScreen } from "@/components/guards/RoleGuard";

// The loop bound and the last-attempt check below are derived from this single constant so they
// can never drift apart. If they did (e.g. loop reduced to `attempt < 1` while the check still
// read `attempt === 1`), the last attempt would never set a status and the guard would render
// `null` forever — a permanently blank screen.
const PROFILE_FETCH_ATTEMPTS = 2;

export type OnboardingStatus = "loading" | "pass" | "redirect" | "blocked";

/**
 * The onboarding decision on its own, so the app shell can fold it into the single
 * gate that owns the loading surface. Splitting it out is what lets ONE loader
 * cover auth + onboarding + capabilities instead of each gate rendering its own at
 * its own moment in its own box.
 */
export function useOnboardingStatus(): OnboardingStatus {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<OnboardingStatus>("loading");

  useEffect(() => {
    if (authLoading || !user) return;

    let cancelled = false;

    (async () => {
      // One retry: a transient network blip should not send an onboarded user through the
      // wizard, but a persistent failure must not let a draft user into the app either.
      for (let attempt = 0; attempt < PROFILE_FETCH_ATTEMPTS; attempt++) {
        try {
          const profile = await UserCacheService.getProfile(user.id);
          if (cancelled) return;

          const entry = appEntryFor(profile);
          setStatus(entry === "app" ? "pass" : entry === "blocked" ? "blocked" : "redirect");
          return;
        } catch {
          if (cancelled) return;
          // Final attempt MUST set a status here — otherwise the guard hangs on a blank screen.
          if (attempt === PROFILE_FETCH_ATTEMPTS - 1) setStatus("redirect");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return authLoading ? "loading" : status;
}

/**
 * Component form, kept for routes that are not inside the app shell and for the
 * tests that exercise the real decision logic.
 *
 * The loading branch renders a real loader now. It used to `return null` with the
 * comment "AuthGuard already shows loader" — which stopped being true the moment
 * AuthGuard started crossfading: by the time this runs AuthGuard is showing a
 * dissolving veil, not a loader, so a profile fetch slower than the veil left a
 * genuinely blank screen that the blank-screen watchdog cannot see (it finds a
 * healthy, opaque, correctly-sized div that happens to contain nothing).
 */
export function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const status = useOnboardingStatus();

  if (status === "loading") return <GuardLoadingScreen />;
  if (status === "blocked") return <Navigate to="/pending-approval" replace />;
  if (status === "redirect") {
    const attempted = `${location.pathname}${location.search}`;
    return <Navigate to={buildOnboardingUrlWithReturn(attempted)} replace />;
  }
  return <>{children}</>;
}
